import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../hooks/useSubscription';
import { useAuth } from '../contexts/AuthContext';
import lumoUpsellService from '../services/lumoUpsellService';
import Button from './ui/Button';
import { Lock, Sparkles } from 'lucide-react';

/**
 * Premium feature gate component
 * Shows strategic Lumo-powered upsell when user doesn't have access
 */
const PremiumGate = ({ feature, children, requiredPlan = 'Pro' }) => {
  const navigate = useNavigate();
  const { hasAccess } = useSubscription();
  const { profile } = useAuth();

  const canAccess = hasAccess(feature);

  // Get feature preview from upsell service
  const featurePreview = lumoUpsellService.getFeaturePreview(feature);

  // Track when user hits premium gate
  useEffect(() => {
    if (!canAccess && feature) {
      lumoUpsellService.trackUpsellEvent('premium_gate_shown', {
        feature,
        requiredPlan,
        currentPlan: profile?.subscription_plan || 'free',
        userLevel: profile?.level || 1
      });
    }
  }, [canAccess, feature, requiredPlan, profile]);

  if (canAccess) {
    return <>{children}</>;
  }

  // Use feature preview if available, otherwise generic gate
  if (featurePreview) {
    return (
      <div className="bg-gradient-to-br from-primary/5 via-accent/5 to-secondary/5 border-2 border-primary/20 rounded-2xl p-8 md:p-12">
        {/* Lumo Avatar */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-lg">
            <Sparkles className="w-10 h-10 text-white" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-3xl font-bold text-foreground mb-3 text-center">
          {featurePreview.title}
        </h2>

        {/* Description */}
        <p className="text-lg text-muted-foreground mb-8 text-center max-w-2xl mx-auto">
          {featurePreview.description}
        </p>

        {/* Benefits List */}
        <div className="bg-card border border-border rounded-xl p-6 mb-8 max-w-3xl mx-auto">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Lock className="w-5 h-5 text-primary" />
            What You'll Unlock:
          </h3>
          <ul className="space-y-3">
            {featurePreview.benefits.map((benefit, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-primary text-sm font-bold">✓</span>
                </div>
                <span className="text-foreground">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Social Proof / Testimonial */}
        {featurePreview.testimonial && (
          <div className="bg-accent/10 border-l-4 border-primary rounded-r-lg p-4 mb-8 max-w-2xl mx-auto">
            <p className="text-foreground italic">"{featurePreview.testimonial}"</p>
          </div>
        )}

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={() => {
              lumoUpsellService.trackUpsellEvent('premium_gate_upgrade_clicked', {
                feature,
                requiredPlan
              });
              navigate('/app/subscription');
            }}
            className="bg-gradient-to-r from-primary to-secondary hover:opacity-90 text-white px-8 py-4 text-lg font-semibold shadow-lg"
          >
            {featurePreview.cta}
          </Button>
          <Button
            onClick={() => {
              lumoUpsellService.trackUpsellEvent('premium_gate_maybe_later', { feature });
              navigate('/');
            }}
            variant="outline"
            className="px-8 py-4"
          >
            Maybe Later
          </Button>
        </div>

        {/* Trust Badge */}
        <p className="text-center text-sm text-muted-foreground mt-6">
          ⚡ Join 1,000+ reps using {requiredPlan} to close more deals
        </p>
      </div>
    );
  }

  // Fallback generic gate
  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-8 text-center">
      <Lock className="w-12 h-12 text-blue-600 mx-auto mb-4" />
      <h2 className="text-2xl font-bold text-gray-900 mb-2">{requiredPlan} Feature</h2>
      <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
        This feature requires upgrading to {requiredPlan}.
      </p>
      <Button 
        onClick={() => navigate('/app/subscription')}
        className="bg-blue-600 hover:bg-blue-700 text-white"
      >
        Upgrade to {requiredPlan}
      </Button>
    </div>
  );
};

export default PremiumGate;
